import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { FileWidget } from './FileWidget/FileWidget';
import { FilePicker, IFilePickerProps, IFilePickerTranslations } from './FilePicker/FilePicker';
import { IGutterProps, useGutterCss } from '../../fundamentals/spaces/space';
import { Label } from '../Label/Label';
import { FileProgress, IFileProgress, IFileProgressProps, IFileProgressTranslations } from './FileProgress/FileProgress';
import { act } from 'react-dom/test-utils';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseDiv } from '../../common/BaseElements';

const SUCCESS_MESSAGE_DURATION_MS = 1000;

export interface IFileUploadProps extends IGutterProps, Omit<IFilePickerProps, 'translations'>, Omit<IFileProgressProps, 'translations'> {
  onFileClicked?: (file: File) => void;
  onFileDeleted?: (file: File) => boolean;
  translations?: IFileUploadTranslations;
  required?: boolean;
}

export interface IFileUploadTranslations extends IFilePickerTranslations, IFileProgressTranslations {
  mainLabel?: string;
  filesLabel?: string;
  deleted?: string;
}

export const fileUploadColours = ({ base, palette, status }: IColoursCollection) => ({
  fileUploadColours: {
    default: {
      border: palette.neutral300,
      background: base.white.colour
    },
    hover: {
      background: palette.neutral15
    },
    active: {
      background: palette.neutral25
    },
    disabled: {
      text: palette.neutral300,
      background: palette.neutral15
    },
    error: {
      border: status.error.colour
    }
  }
});

const FileUploadContainer = styled(BaseDiv)<IFileUploadProps>`
  ${useGutterCss()}
`;

export const FileUpload: React.FC<IFileUploadProps> = ({ onFileClicked, onFileDeleted, onFilesSelected, translations, progress = [], ...rest }) => {
  const { generateTestId } = useTestId(rest, 'FileUpload');
  const [successfulUploads, setSuccessfulUploads] = useState<(IFileProgress & { hide: boolean })[]>([]);
  const successfulUploadsRef = useRef(successfulUploads);

  const handleFilesSelected = (acceptedFiles: File[]) => acceptedFiles.length > 0 && onFilesSelected?.(acceptedFiles);
  const handleFileClicked = (clickedFileName?: string) => act(() => onFileClicked?.(progress.find(({ file }) => file.name === clickedFileName)!.file));
  const handleFileDeleted = (deletedFileName?: string) => {
    const fileToDelete = progress.find(({ file }) => file.name === deletedFileName)?.file;
    if (fileToDelete && onFileDeleted?.(fileToDelete)) {
      const updated = successfulUploadsRef.current.filter(({ file }) => file.name !== deletedFileName);
      setSuccessfulUploads(updated);
      successfulUploadsRef.current = updated;
    }
  };

  const pickerProps: IFilePickerProps = {
    onFilesSelected: handleFilesSelected,
    acceptedFileTypes: rest.acceptedFileTypes,
    disabled: rest.disabled,
    maxSize: rest.maxSize,
    multiple: rest.multiple,
    translations: translations,
    errors: rest.errors
  };

  useEffect(() => {
    const newSuccessfulUploads = progress
      .filter(
        ({ progress: progressValue, file }) =>
          progressValue === 1 && !successfulUploadsRef.current.some(({ file: successfulUploadFile }) => file.name === successfulUploadFile.name)
      )
      .map((fileProgress) => ({
        ...fileProgress,
        succeededAt: Date.now(),
        hide: false
      }));
    if (newSuccessfulUploads?.length! > 0) {
      const toUpdate = [...successfulUploadsRef.current, ...newSuccessfulUploads!];
      setSuccessfulUploads(toUpdate);
      successfulUploadsRef.current = toUpdate;

      const timeoutId = setTimeout(() => {
        const updated = successfulUploadsRef.current.map((successfulUpload) => ({
          ...successfulUpload,
          hide: true
        }));
        setSuccessfulUploads(updated);
        successfulUploadsRef.current = updated;
      }, SUCCESS_MESSAGE_DURATION_MS);
      return () => clearTimeout(timeoutId);
    }
  }, [progress]);

  return (
    <FileUploadContainer {...generateTestId()} {...rest}>
      {translations?.mainLabel && (
        <Label required={rest.required} {...generateTestId('MainLabel')}>
          {translations?.mainLabel}
        </Label>
      )}
      <FilePicker {...generateTestId('FilePicker')} {...pickerProps} />
      <FileProgress
        progress={[...progress.filter((fileProgress) => fileProgress.progress < 1), ...successfulUploads.filter((successfulUpload) => !successfulUpload.hide)]}
        translations={translations}
      ></FileProgress>
      {progress.some((fileProgress) => fileProgress.progress === 1) && translations?.filesLabel && (
        <Label {...generateTestId('FilesLabel')}>{translations?.filesLabel}</Label>
      )}
      {progress
        .filter(({ progress: fileProgress }) => fileProgress === 1)
        .map(({ file }, key) => (
          <FileWidget
            {...generateTestId(`FileWidget.${key}`)}
            name={file.name}
            size={file.size}
            onFileClicked={onFileClicked && ((fileName?: string) => handleFileClicked(fileName))}
            onFileDeleted={onFileDeleted && ((fileName?: string) => handleFileDeleted(fileName))}
            key={file.name}
          ></FileWidget>
        ))}
    </FileUploadContainer>
  );
};
